import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, TrendingUp, TrendingDown, Minus, Pin, Plus, Trash2 } from "lucide-react";
import { getStoreId } from "../utils/auth";
import { showQuotaExceededAlert } from "../utils/quotaAlert";
import { showToast } from "../utils/toast";
import "./SEOOptimizer.css";

const MAX_COMPETITORS = 3;

const KeywordTracker = () => {
  const { t } = useTranslation();
  const [keywords, setKeywords] = useState([]);
  const [storeDomain, setStoreDomain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKeywordId, setSelectedKeywordId] = useState(null);
  const [rankHistory, setRankHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [serpSnapshot, setSerpSnapshot] = useState([]);
  const [serpCheckedAt, setSerpCheckedAt] = useState(null);
  const [serpLoading, setSerpLoading] = useState(false);
  const [competitorDomains, setCompetitorDomains] = useState([]);
  const [newCompetitorDomain, setNewCompetitorDomain] = useState('');
  const [addingCompetitor, setAddingCompetitor] = useState(false);
  const [deletingKeywordId, setDeletingKeywordId] = useState(null);
  const [quota, setQuota] = useState(null);
  const [keywordToDelete, setKeywordToDelete] = useState(null);
  const [showAddKeywordModal, setShowAddKeywordModal] = useState(false);
  const [addKeywordKeyword, setAddKeywordKeyword] = useState('');
  const [addKeywordCountry, setAddKeywordCountry] = useState('United States');
  const [addKeywordDevice, setAddKeywordDevice] = useState('Desktop');
  const [addKeywordEngine, setAddKeywordEngine] = useState('google.com');
  const [addKeywordLanguage, setAddKeywordLanguage] = useState('English');
  const [addKeywordSubmitting, setAddKeywordSubmitting] = useState(false);
  const [rankOptions, setRankOptions] = useState({ countries: [], languages: [] });

  const fetchKeywordsInFlightRef = useRef(false);
  const fetchRankOptionsInFlightRef = useRef(false);
  const fetchCompetitorDomainsInFlightRef = useRef(false);

  const apiUrl = import.meta.env.VITE_BACKEND_URL;
  const hashSiteId = getStoreId();

  const fetchKeywords = async () => {
    if (!hashSiteId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/api/seo-optimizer/keywords?id=${hashSiteId}`);
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to load keywords');
      const data = await res.json();
      setKeywords(data.keywords || []);
      setStoreDomain(data.storeDomain || null);
      setQuota(data.quota || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitorDomains = async () => {
    if (!hashSiteId) return;
    try {
      const res = await fetch(`${apiUrl}/api/seo-optimizer/competitor-domains?id=${hashSiteId}`);
      if (!res.ok) return;
      const data = await res.json();
      setCompetitorDomains(data.competitorDomains || []);
    } catch (_) {
      setCompetitorDomains([]);
    }
  };

  const fetchRankOptions = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/seo-optimizer/rank-options`);
      if (!res.ok) return;
      const data = await res.json();
      setRankOptions({
        countries: data.countries || [],
        languages: data.languages || []
      });
    } catch (_) {
      setRankOptions({ countries: [], languages: [] });
    }
  };

  useEffect(() => {
    if (!hashSiteId) return;
    if (fetchKeywordsInFlightRef.current) return;
    fetchKeywordsInFlightRef.current = true;
    fetchKeywords().finally(() => { fetchKeywordsInFlightRef.current = false; });
  }, [hashSiteId]);

  useEffect(() => {
    if (fetchRankOptionsInFlightRef.current) return;
    fetchRankOptionsInFlightRef.current = true;
    fetchRankOptions().finally(() => { fetchRankOptionsInFlightRef.current = false; });
  }, []);

  useEffect(() => {
    if (!hashSiteId) return;
    if (fetchCompetitorDomainsInFlightRef.current) return;
    fetchCompetitorDomainsInFlightRef.current = true;
    fetchCompetitorDomains().finally(() => { fetchCompetitorDomainsInFlightRef.current = false; });
  }, [hashSiteId]);

  const fetchRankHistory = async (keywordId) => {
    if (!hashSiteId || !keywordId) return;
    setHistoryLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/seo-optimizer/rank-history?id=${hashSiteId}&keywordId=${keywordId}&limit=50`);
      if (!res.ok) throw new Error('Failed to load history');
      const data = await res.json();
      setRankHistory(data.rankHistory || []);
    } catch (err) {
      setRankHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchSerpSnapshot = async (keywordId) => {
    if (!hashSiteId || !keywordId) return;
    setSerpLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/seo-optimizer/serp-snapshot?id=${hashSiteId}&keywordId=${keywordId}`);
      if (!res.ok) throw new Error('Failed to load SERP');
      const data = await res.json();
      setSerpSnapshot(data.serpSnapshot || []);
      setSerpCheckedAt(data.checkedAt || null);
    } catch (err) {
      setSerpSnapshot([]);
      setSerpCheckedAt(null);
    } finally {
      setSerpLoading(false);
    }
  };

  useEffect(() => {
    if (selectedKeywordId) {
      fetchRankHistory(selectedKeywordId);
      fetchSerpSnapshot(selectedKeywordId);
    } else {
      setRankHistory([]);
      setSerpSnapshot([]);
      setSerpCheckedAt(null);
    }
  }, [selectedKeywordId, hashSiteId]);

  const isOurDomain = (domain) => {
    if (!storeDomain || !domain) return false;
    return domain.toLowerCase() === storeDomain.toLowerCase();
  };

  const isPinned = (domain) => competitorDomains.some((c) => (c.domain || '').toLowerCase() === (domain || '').toLowerCase());
  const atCompetitorLimit = competitorDomains.length >= MAX_COMPETITORS;
  const atKeywordQuota = quota && !quota.unlimited && typeof quota.remaining === 'number' && quota.remaining <= 0;
  const nearKeywordQuota = quota && !quota.unlimited && typeof quota.remaining === 'number' && quota.remaining > 0 && quota.remaining <= 2;

  const addCompetitor = async (domain) => {
    if (!hashSiteId || !domain) return;
    const clean = String(domain).replace(/^https?:\/\//i, '').split('/')[0].trim();
    if (!clean) return;
    setAddingCompetitor(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/api/seo-optimizer/competitor-domains`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: hashSiteId, domain: clean })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add');
      setNewCompetitorDomain('');
      await fetchCompetitorDomains();
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingCompetitor(false);
    }
  };

  const handleAddCompetitorSubmit = (e) => {
    e.preventDefault();
    addCompetitor(newCompetitorDomain);
  };

  const handleAddKeywordSubmit = async () => {
    const keywordTrimmed = (addKeywordKeyword || '').trim();
    if (!keywordTrimmed) {
      showToast(t('seoOptimizerDetail.keywordRequired'), 'error');
      return;
    }
    if (/[,]/.test(keywordTrimmed)) {
      showToast(t('seoOptimizerDetail.onlyOneKeywordAllowed'), 'error');
      return;
    }
    setAddKeywordSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/seo-optimizer/add-keyword?id=${hashSiteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keyword: keywordTrimmed,
          country: addKeywordCountry,
          device: addKeywordDevice,
          engine: addKeywordEngine,
          language: addKeywordLanguage
        })
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 403 && data.code === 'QUOTA_EXCEEDED') {
          const msg = data.quota
            ? t('seoOptimizerDetail.keywordQuotaExceeded', { used: data.quota.used, limit: data.quota.limit })
            : t('seoOptimizerDetail.keywordQuotaExceededGeneric');
          await showQuotaExceededAlert(t('common.quotaExceededTitle'), msg, t('common.ok'));
          return;
        }
        throw new Error(data.error || 'Failed to add keyword');
      }
      showToast(t('seoOptimizerDetail.keywordAdded'), 'success');
      setShowAddKeywordModal(false);
      setAddKeywordKeyword('');
      await fetchKeywords();
    } catch (err) {
      showToast(err.message || t('seoOptimizerDetail.failedToAddKeyword'), 'error');
    } finally {
      setAddKeywordSubmitting(false);
    }
  };

  const removeCompetitor = async (competitorId) => {
    if (!hashSiteId) return;
    try {
      const res = await fetch(`${apiUrl}/api/seo-optimizer/competitor-domains/${competitorId}?id=${hashSiteId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove');
      await fetchCompetitorDomains();
    } catch (err) {
      setError(err.message);
    }
  };

  const removeKeyword = async (keywordId) => {
    if (!hashSiteId || !keywordId) return;
    setDeletingKeywordId(keywordId);
    try {
      const res = await fetch(`${apiUrl}/api/seo-optimizer/keywords/${keywordId}?id=${hashSiteId}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to delete keyword');
      if (selectedKeywordId === keywordId) setSelectedKeywordId(null);
      await fetchKeywords();
      showToast(t('seoOptimizer.keywordTrackerDeleted'), 'success');
    } catch (err) {
      showToast(err.message || t('seoOptimizer.keywordTrackerDeleteFailed'), 'error');
    } finally {
      setDeletingKeywordId(null);
    }
  };

  const handleConfirmDeleteKeyword = async () => {
    if (!keywordToDelete) return;
    const id = keywordToDelete.id;
    await removeKeyword(id);
    setKeywordToDelete(null);
  };

  const getChange = (row) => {
    const curr = row.latest_position != null ? Number(row.latest_position) : null;
    const prev = row.previous_position != null ? Number(row.previous_position) : null;
    if (prev == null || curr == null) return 'new';
    if (curr < prev) return 'up';
    if (curr > prev) return 'down';
    return 'same';
  };

  const winning = keywords.filter((k) => getChange(k) === 'up');
  const losing = keywords.filter((k) => getChange(k) === 'down');
  const selectedKeyword = keywords.find((k) => k.id === selectedKeywordId);

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (!hashSiteId) {
    return (
      <div className="card">
        <div className="card-body text-center py-5 text-muted">
          {t('seoOptimizerDetail.storeIdNotFound')}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 className="mb-0 fw-bold">{t('seoOptimizer.keywordTrackerTitle')}</h5>
        <div className="d-flex align-items-center gap-2">
          {quota && !quota.unlimited && (
            <span className="small text-muted">
              {t('seoOptimizer.keywordTrackerQuotaUsage', { used: quota.used, limit: quota.limit })}
            </span>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
            onClick={() => setShowAddKeywordModal(true)}
            disabled={loading || atKeywordQuota}
            title={atKeywordQuota ? t('seoOptimizer.keywordTrackerQuotaReached') : ''}
          >
            <Plus size={14} />
            {t('seoOptimizer.keywordTrackerAddKeyword')}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
            onClick={fetchKeywords}
            disabled={loading}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            ) : (
              <RefreshCw size={14} />
            )}
            {t('seoOptimizer.refresh')}
          </button>
        </div>
      </div>

      {/* Info: What Keyword Tracker does */}
      <div className="card border mb-3 border-start border-2 rounded-end" style={{ backgroundColor: '#f8f9fa', borderLeftColor: '#adb5bd' }}>
        <div className="card-body py-2 px-3">
          <ul className="mb-0 ps-3" style={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
            <li className="fw-semibold mb-1"><span className="text-dark">{t('seoOptimizer.infoCardKeyword1Label')}</span> {t('seoOptimizer.infoCardKeyword1Desc')}</li>
            <li className="fw-semibold mb-1"><span className="text-dark">{t('seoOptimizer.infoCardKeyword2Label')}</span> {t('seoOptimizer.infoCardKeyword2Desc')}</li>
            <li className="fw-semibold"><span className="text-dark">{t('seoOptimizer.infoCardKeyword3Label')}</span> {t('seoOptimizer.infoCardKeyword3Desc')}</li>
          </ul>
        </div>
      </div>

      {atKeywordQuota && (
        <div className="alert alert-warning mb-3 d-flex align-items-center gap-2">
          <span>{t('seoOptimizer.keywordTrackerQuotaReached')}</span>
          {quota?.accountType === 'trial' && (
            <span className="small">{t('seoOptimizer.keywordTrackerQuotaUpgradeHint')}</span>
          )}
        </div>
      )}
      {nearKeywordQuota && !atKeywordQuota && (
        <div className="alert alert-info py-2 mb-3 small">
          {t('seoOptimizer.keywordTrackerQuotaNearLimit', { remaining: quota?.remaining ?? 0 })}
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : keywords.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5 text-muted">
            <p className="mb-0">{t('seoOptimizer.keywordTrackerEmpty')}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Winning vs Losing */}
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <div className="card border-0 shadow-sm h-100 keyword-tracker-stat-card">
                <div className="card-body d-flex align-items-center gap-1">
                  <TrendingUp size={20} className="text-success flex-shrink-0" />
                  <div>
                    <div className="small text-muted">{t('seoOptimizer.keywordTrackerWinning')}</div>
                    <div className="fw-bold text-success">{winning.length}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-0 shadow-sm h-100 keyword-tracker-stat-card">
                <div className="card-body d-flex align-items-center gap-1">
                  <TrendingDown size={20} className="text-danger flex-shrink-0" />
                  <div>
                    <div className="small text-muted">{t('seoOptimizer.keywordTrackerLosing')}</div>
                    <div className="fw-bold text-danger">{losing.length}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pinned competitors */}
          <div className="card mb-3 keyword-tracker-pinned-card">
            <div className="card-header bg-white py-2">{t('seoOptimizer.keywordTrackerPinnedCompetitors')}</div>
            <div className="card-body py-2">
              <form onSubmit={handleAddCompetitorSubmit} className="d-flex flex-wrap gap-2 mb-2">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ maxWidth: '260px' }}
                  placeholder={t('seoOptimizer.keywordTrackerAddCompetitorPlaceholder')}
                  value={newCompetitorDomain}
                  onChange={(e) => setNewCompetitorDomain(e.target.value)}
                  disabled={addingCompetitor}
                />
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={addingCompetitor || !newCompetitorDomain.trim() || atCompetitorLimit}
                >
                  {addingCompetitor ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  ) : (
                    t('seoOptimizer.keywordTrackerAddCompetitor')
                  )}
                </button>
              </form>
              {atCompetitorLimit && (
                <p className="small text-warning mb-2">{t('seoOptimizer.keywordTrackerMaxCompetitors')}</p>
              )}
              <p className="small text-muted mb-2">
                {t('seoOptimizer.keywordTrackerPinnedCompetitorsHint')}
              </p>
              {competitorDomains.length > 0 ? (
                <ul className="list-group list-group-flush list-group-horizontal flex-wrap gap-2 mb-0">
                  {competitorDomains.map((c) => (
                    <li key={c.id} className="list-group-item d-flex align-items-center gap-2 border rounded px-2 py-1">
                      <span className="small">{c.label || c.domain}</span>
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-danger p-0"
                        onClick={() => removeCompetitor(c.id)}
                        title={t('seoOptimizer.keywordTrackerRemove')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="small text-muted mb-0">{t('seoOptimizer.keywordTrackerNoPinnedYet')}</p>
              )}
            </div>
          </div>

          {/* Keywords table – spacious, well-designed */}
          <p className="small text-muted mb-3">{t('seoOptimizer.keywordTrackerHowToRead')}</p>
          <div className="card mb-3 border-0 shadow-sm overflow-hidden">
            <div className="table-responsive">
              <table className="table keyword-tracker-table mb-0">
                <thead>
                  <tr>
                    <th className="keyword-tracker-th">{t('seoOptimizer.keywordTrackerKeyword')}</th>
                    <th className="keyword-tracker-th text-center">{t('seoOptimizer.keywordTrackerYourRank')}</th>
                    <th className="keyword-tracker-th text-center">{t('seoOptimizer.keywordTrackerWasRank')}</th>
                    <th className="keyword-tracker-th text-center">{t('seoOptimizer.keywordTrackerChange')}</th>
                    <th className="keyword-tracker-th">{t('seoOptimizer.keywordTrackerSearchSettings')}</th>
                    <th className="keyword-tracker-th">{t('seoOptimizer.keywordTrackerCompetitorsInTop')}</th>
                    <th className="keyword-tracker-th">{t('seoOptimizer.keywordTrackerLastChecked')}</th>
                    <th className="keyword-tracker-th" style={{ width: '140px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {keywords.map((row) => {
                    const change = getChange(row);
                    const isExpanded = selectedKeywordId === row.id;
                    const yourRank = row.latest_position != null ? row.latest_position : null;
                    const wasRank = row.previous_position != null ? row.previous_position : null;
                    return (
                      <tr
                        key={row.id}
                        className={`keyword-tracker-tr ${isExpanded ? 'keyword-tracker-tr-expanded' : ''}`}
                      >
                        <td className="keyword-tracker-td">
                          <span className="fw-semibold text-dark">{row.keyword}</span>
                        </td>
                        <td className="keyword-tracker-td text-center">
                          {yourRank != null ? (
                            <span className="keyword-tracker-rank fw-bold text-primary">#{yourRank}</span>
                          ) : (
                            <span className="small text-muted">{t('seoOptimizer.keywordTrackerNotInTop')}</span>
                          )}
                        </td>
                        <td className="keyword-tracker-td text-center">
                          {wasRank != null ? <span className="text-muted">#{wasRank}</span> : '—'}
                        </td>
                        <td className="keyword-tracker-td text-center">
                          {change === 'up' && <span className="d-inline-flex align-items-center gap-1 text-success fw-medium small"><TrendingUp size={16} /> {t('seoOptimizer.keywordTrackerImproved')}</span>}
                          {change === 'down' && <span className="d-inline-flex align-items-center gap-1 text-danger fw-medium small"><TrendingDown size={16} /> {t('seoOptimizer.keywordTrackerDropped')}</span>}
                          {change === 'same' && <span className="d-inline-flex align-items-center gap-1 text-muted small"><Minus size={16} /> {t('seoOptimizer.keywordTrackerSame')}</span>}
                          {change === 'new' && <span className="text-secondary small">{t('seoOptimizer.keywordTrackerNew')}</span>}
                        </td>
                        <td className="keyword-tracker-td small text-muted">
                          {row.country} · {row.device} · {row.engine}
                        </td>
                        <td className="keyword-tracker-td">
                          {row.competitor_ranks && row.competitor_ranks.length > 0 ? (
                            <span className="d-flex flex-wrap gap-1">
                              {row.competitor_ranks.map((c) => (
                                <span key={c.domain} className="badge bg-secondary bg-opacity-15 text-dark py-1 px-2 small">
                                  {c.label || c.domain} <strong>#{c.position}</strong>
                                </span>
                              ))}
                            </span>
                          ) : (
                            <span className="small text-muted">—</span>
                          )}
                        </td>
                        <td className="keyword-tracker-td small text-muted">
                          {row.last_checked_at ? formatDate(row.last_checked_at) : '—'}
                        </td>
                        <td className="keyword-tracker-td">
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => setSelectedKeywordId(isExpanded ? null : row.id)}
                            >
                              {isExpanded ? t('seoOptimizer.keywordTrackerHideHistory') : t('seoOptimizer.keywordTrackerViewHistory')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-danger p-0"
                              onClick={() => setKeywordToDelete({ id: row.id, keyword: row.keyword })}
                              disabled={deletingKeywordId === row.id}
                              title={t('seoOptimizer.keywordTrackerDelete')}
                            >
                              {deletingKeywordId === row.id ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Historical trend + SERP snapshot for selected keyword */}
          {selectedKeywordId && (
            <>
              <div className="card mb-3">
                <div className="card-header bg-white d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">
                    {t('seoOptimizer.keywordTrackerHistory')}: {selectedKeyword?.keyword || ''}
                  </span>
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-decoration-none"
                    onClick={() => setSelectedKeywordId(null)}
                  >
                    {t('common.close')}
                  </button>
                </div>
                <div className="card-body p-0">
                  {historyLoading ? (
                    <div className="text-center py-4">
                      <span className="spinner-border spinner-border-sm" role="status" />
                    </div>
                  ) : rankHistory.length === 0 ? (
                    <div className="text-center py-4 text-muted small">{t('seoOptimizer.keywordTrackerNoHistory')}</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t('seoOptimizer.keywordTrackerCheckedAt')}</th>
                            <th className="text-center">{t('seoOptimizer.keywordTrackerPosition')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rankHistory.map((h) => (
                            <tr key={h.id}>
                              <td className="small">{formatDate(h.checked_at)}</td>
                              <td className="text-center">{h.position != null ? h.position : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* SERP snapshot + pinned competitors */}
              <div className="card mb-3">
                <div className="card-header bg-white">
                  {t('seoOptimizer.keywordTrackerSerpSnapshot')}
                  {serpCheckedAt && (
                    <span className="small text-muted ms-2">
                      {formatDate(serpCheckedAt)}
                    </span>
                  )}
                </div>
                <div className="card-body p-0">
                  {serpLoading ? (
                    <div className="text-center py-4">
                      <span className="spinner-border spinner-border-sm" role="status" />
                    </div>
                  ) : serpSnapshot.length === 0 ? (
                    <div className="text-center py-4 text-muted small">{t('seoOptimizer.keywordTrackerNoSerpData')}</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="text-center" style={{ width: '80px' }}>#</th>
                            <th>{t('seoOptimizer.keywordTrackerDomain')}</th>
                            <th>{t('seoOptimizer.keywordTrackerTitle')}</th>
                            <th style={{ width: '120px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {serpSnapshot.map((row) => {
                            const ours = isOurDomain(row.domain);
                            const pinned = isPinned(row.domain);
                            return (
                              <tr key={row.id} className={ours ? 'table-primary' : pinned ? 'table-light' : ''}>
                                <td className="text-center">{row.position}</td>
                                <td>
                                  {row.domain}
                                  {ours && <span className="badge bg-primary ms-1">{t('seoOptimizer.keywordTrackerYou')}</span>}
                                  {!ours && pinned && <span className="badge bg-secondary ms-1">{t('seoOptimizer.keywordTrackerPinned')}</span>}
                                </td>
                                <td className="small text-muted text-truncate" style={{ maxWidth: '280px' }} title={row.title || ''}>
                                  {row.title || '—'}
                                </td>
                                <td>
                                  {!ours && !pinned && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                                      onClick={() => addCompetitor(row.domain)}
                                      disabled={atCompetitorLimit}
                                      title={atCompetitorLimit ? t('seoOptimizer.keywordTrackerMaxCompetitors') : ''}
                                    >
                                      <Pin size={12} />
                                      {t('seoOptimizer.keywordTrackerPinCompetitor')}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

            </>
          )}
        </>
      )}

      {/* Delete keyword confirmation modal */}
      {keywordToDelete && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          onClick={() => setKeywordToDelete(null)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h5 className="modal-title fw-bold">{t('seoOptimizer.keywordTrackerDeleteModalTitle')}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setKeywordToDelete(null)}
                  aria-label={t('common.close')}
                />
              </div>
              <div className="modal-body">
                <p className="mb-0">{t('seoOptimizer.keywordTrackerDeleteConfirm')}</p>
                {keywordToDelete.keyword && (
                  <p className="mb-0 mt-2 fw-semibold text-dark">"{keywordToDelete.keyword}"</p>
                )}
              </div>
              <div className="modal-footer border-top">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setKeywordToDelete(null)}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmDeleteKeyword}
                  disabled={deletingKeywordId === keywordToDelete?.id}
                >
                  {deletingKeywordId === keywordToDelete?.id ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      {t('common.saving')}
                    </>
                  ) : (
                    t('seoOptimizer.keywordTrackerDelete')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Keyword Modal */}
      {showAddKeywordModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          onClick={() => setShowAddKeywordModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h5 className="modal-title fw-bold">{t('seoOptimizerDetail.addKeywordModalTitle')}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddKeywordModal(false)}
                  aria-label={t('common.close')}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label small fw-bold">{t('seoOptimizerDetail.enterKeyword')}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={addKeywordKeyword}
                    onChange={(e) => setAddKeywordKeyword(e.target.value)}
                    placeholder={t('seoOptimizerDetail.enterKeywordPlaceholder')}
                  />
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-bold">{t('seoOptimizerDetail.country')}</label>
                    <select
                      className="form-select form-select-sm"
                      value={addKeywordCountry}
                      onChange={(e) => setAddKeywordCountry(e.target.value)}
                    >
                      {rankOptions.countries.length > 0
                        ? rankOptions.countries.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))
                        : (
                            <>
                              <option value="United States">United States</option>
                              <option value="United Kingdom">United Kingdom</option>
                              <option value="Canada">Canada</option>
                              <option value="Australia">Australia</option>
                              <option value="Germany">Germany</option>
                              <option value="France">France</option>
                              <option value="Spain">Spain</option>
                              <option value="Italy">Italy</option>
                              <option value="Netherlands">Netherlands</option>
                              <option value="India">India</option>
                            </>
                          )}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-bold">{t('seoOptimizerDetail.device')}</label>
                    <select
                      className="form-select form-select-sm"
                      value={addKeywordDevice}
                      onChange={(e) => setAddKeywordDevice(e.target.value)}
                    >
                      <option value="Desktop">Desktop</option>
                      <option value="Mobile">Mobile</option>
                    </select>
                  </div>
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-bold">{t('seoOptimizerDetail.searchEngine')}</label>
                    <select
                      className="form-select form-select-sm"
                      value={addKeywordEngine}
                      onChange={(e) => setAddKeywordEngine(e.target.value)}
                    >
                      <option value="google.com">google.com</option>
                      <option value="google.co.uk">google.co.uk</option>
                      <option value="google.ca">google.ca</option>
                      <option value="google.com.au">google.com.au</option>
                      <option value="google.de">google.de</option>
                      <option value="google.fr">google.fr</option>
                      <option value="google.es">google.es</option>
                      <option value="bing.com">bing.com</option>
                      <option value="duckduckgo.com">duckduckgo.com</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-bold">{t('seoOptimizerDetail.language')}</label>
                    <select
                      className="form-select form-select-sm"
                      value={addKeywordLanguage}
                      onChange={(e) => setAddKeywordLanguage(e.target.value)}
                    >
                      {rankOptions.languages.length > 0
                        ? rankOptions.languages.map((lang) => (
                            <option key={lang} value={lang}>{lang}</option>
                          ))
                        : (
                            <>
                              <option value="English">English</option>
                              <option value="Spanish">Spanish</option>
                              <option value="French">French</option>
                              <option value="German">German</option>
                              <option value="Italian">Italian</option>
                              <option value="Portuguese">Portuguese</option>
                              <option value="Dutch">Dutch</option>
                            </>
                          )}
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddKeywordModal(false)}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-dark"
                  onClick={handleAddKeywordSubmit}
                  disabled={addKeywordSubmitting || !(addKeywordKeyword || '').trim()}
                >
                  {addKeywordSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      {t('common.saving')}
                    </>
                  ) : (
                    t('seoOptimizerDetail.addKeyword')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeywordTracker;
